import Axios, { AxiosError } from 'axios';
import React, { FC, SyntheticEvent, useState } from 'react';
import { Form, Button } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import validate from 'validate.js';
import { providePhoneConstraints } from '../constraints/ProvidePhone';
import { useRecaptcha } from '../hooks/useRecaptcha';
import Alert, { AlertType, IAlertProps } from './components/Alert';

import AuthLogo from './components/AuthLogo';
import { UseTranslate } from '../hooks/Translate';

type Params = {
  userId: string;
};

const ProvidePhonePage: FC = () => {
  const params = useParams<Params>();
  const navigate = useNavigate();
  const [errors, setErrors] = useState<{ [key: string]: string[] }>({});
  const [validated, setValidated] = useState(false);
  const [alertProps, setAlertProps] = useState<IAlertProps>({
    type: AlertType.NONE,
  });

  const [getRecaptchaToken] = useRecaptcha('userProvidePhone');
  const [translate] = UseTranslate();

  const onFormSubmit = (e: SyntheticEvent) => {
    e.preventDefault();
    const fd = new FormData(e.target as HTMLFormElement);
    const values = Object.fromEntries<any>(fd.entries());
    const errors = validate(values, providePhoneConstraints(translate('cas.registration.phoneStart'), translate('cas.registration.phoneLength'), translate('cas.registration.blankPhone')), { fullMessages: false });

    if (errors) {
      for (const key in errors) {
        errors[key] = errors[key][0];
      }

      setErrors(errors);
      setAlertProps({
        type: AlertType.ERROR,
        title: translate('cas.registration.errorNotice'),
        message: translate('cas.registration.registerCheckErrorFields'),
      });
    } else {
      setErrors({});
      setAlertProps({ type: AlertType.NONE });
      updatePhoneAndSendCode(values);
    }

    setValidated(true);
  };

  const updatePhoneAndSendCode = async (data: any) => {
    try {
      const userId = params.userId;
      const url = `${process.env.REACT_APP_CAS_API}/users/register/${userId}/phone`;
      const token = await getRecaptchaToken();
      await Axios.put(
        url,
        {
          phoneNumber: data.phone,
        },
        {
          headers: {
            token: token ? token.toString() : null
          },
        }
      );
      let path = `/verify/contact/${userId}`;
      navigate(path);
    } catch (e) {
      let message;
      if (e instanceof AxiosError && e.response) {
        message = e.response.data.message
      }
      setAlertProps({
        type: AlertType.ERROR,
        title: translate('cas.registration.errorNotice'),
        message
      });
    }
  };

  const validateOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const errors = validate(
      { phone: event.currentTarget.value },
      providePhoneConstraints(translate('cas.registration.phoneStart'), translate('cas.registration.phoneLength'), translate('cas.registration.blankPhone')),
      { fullMessages: false }
    );
    if (errors) {
      for (const key in errors) {
        errors[key] = errors[key][0];
      }

      setErrors(errors);
    } else {
      setErrors({});
    }
  };

  const validateOnKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (['+', '-', '.', 'e'].indexOf(event.key) !== -1) {
      event.preventDefault();
    }
  };

  return (
    <div className="auth-wrapper">
      <div className="text-center">
        <AuthLogo />
      </div>

      <div className="mb-4">
        <div className="small">
          <span className="text-muted">{translate('cas.registration.addPhoneNumber')}</span>
        </div>
      </div>

      <Alert
        type={alertProps!.type}
        title={alertProps!.title}
        message={alertProps!.message}
      />
      <Form validated={validated} onSubmit={onFormSubmit} noValidate>
        <Form.Group className='mb-3' controlId="phone">
          <Form.Label className="small phone-icon">{translate('cas.registration.phoneNumber')}</Form.Label>
          <Form.Control
            type="number"
            placeholder="5********"
            name="phone"
            isInvalid={'phone' in errors}
            required
            onChange={validateOnChange}
            onKeyDown={validateOnKeyDown}
          />
          <Form.Control.Feedback type="invalid">
            {errors.phone}
          </Form.Control.Feedback>
        </Form.Group>

        <Button variant="primary" type="submit" className="w-100">
          {translate('cas.registration.add')}
        </Button>
      </Form>
    </div>
  );
};

export default ProvidePhonePage;
